import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useReduxState } from 'hooks';
import { getBlockFileForLanguage } from 'lib/courses';
import { FileUploader } from 'components/common/uploader';
import {
  TaskIsTaskDialog,
  TaskPointWeightSelector
} from 'components/common/tasks';
import {
  defaultBlockData,
  EditorBlockTranslationReplace
} from 'components/editor/blocks';
import {
  EditorBlockEmbedPreview,
  EditorBlockEmbedFileControls
} from 'components/editor/blocks/embed';

import type { BlockH5P } from 'shared-types';
import type { BlockProps } from 'components/editor/blocks/EditorBlock';
import type { BlockEmbedStateProps } from '../embed/EditorBlockEmbed';

const MAX_UPLOAD_FILE_POLLING_TRIES = 10;

const EditorBlockH5P = ({
  blockId,
  data,
  isPreview,
  languageVersion,
  updateData,
  ...stateProps
}: BlockProps<BlockH5P> & BlockEmbedStateProps) => {
  const {
    messages,
    theme,
    pageState: { course }
  } = useReduxState();

  const { editorLanguageActive } = stateProps;

  const [loading, setLoading] = useState(false);
  const [errorCount, setErrorCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [showIsTaskDialog, setShowIsTaskDialog] = useState(false);
  const [showLanguageSpecificUploader, setShowLanguageSpecificUploader] =
    useState(false);

  const h5pUploadRefreshTimeout = useRef<number | null>(null);

  const {
    activeLanguage,
    shouldShowDefaultFile,
    fileUrl: h5pUrl
  } = getBlockFileForLanguage({
    data,
    courseLanguage: course.language,
    editorActiveLanguageVersion: editorLanguageActive,
    showLanguageSpecificUploader
  });

  useEffect(() => {
    return () => {
      if (h5pUploadRefreshTimeout.current) {
        clearTimeout(h5pUploadRefreshTimeout.current);
      }
    };
  }, []);

  const handleIsTask = (value: boolean) => {
    updateData({
      ...data,
      isTask: value,
      ...(value ? {} : { isMandatory: false, requireAnswerToProceed: false })
    });
    setShowIsTaskDialog(false);
  };

  const handlePointsWeight = (value: number) => {
    if (isEditingLanguageVersion) return;
    updateData({ ...data, pointsWeight: value });
  };

  const handleUploadError = () => {
    setError(messages.editorBlockEmbedH5PFileNotSupported);
  };

  const setH5PUploadRefresh = async (url: string) => {
    try {
      await axios.get(`${url}/h5p.json`, { responseType: 'blob' });
      await axios.get(`${url}/content/content.json`, { responseType: 'blob' });
      setLoading(false);
    } catch {
      if (errorCount > MAX_UPLOAD_FILE_POLLING_TRIES) {
        setLoading(false);
      } else {
        setErrorCount((prev) => prev + 1);
        h5pUploadRefreshTimeout.current = window.setTimeout(() => {
          setH5PUploadRefresh(url);
        }, 1000);
      }
    }
  };

  const handleUploadSuccess = (values: { url: string; title: string }) => {
    const baseUrl = values.url
      .slice(0, values.url.lastIndexOf('/') + 1)
      .replace('/compressed/', '/decompressed/')
      .slice(0, -1);

    updateData({
      ...data,
      locales: {
        ...data.locales,
        [languageVersion]: {
          ...data.locales[languageVersion],
          enableFullScreen: 'enabled',
          file: {
            baseUrl,
            compressedUrl: values.url,
            title: values.title,
            url: baseUrl
          }
        }
      }
    });
    setShowLanguageSpecificUploader(false);
    setLoading(true);

    h5pUploadRefreshTimeout.current = window.setTimeout(() => {
      setH5PUploadRefresh(baseUrl);
    }, 500);
    if (!Boolean(editorLanguageActive)) setShowIsTaskDialog(true);
  };

  const handleReplace = () => {
    setShowLanguageSpecificUploader(true);
  };

  const handleReplaceCancel = () => {
    setShowLanguageSpecificUploader(false);
  };

  const handleRemove = () => {
    updateData({
      ...data,
      locales: {
        ...data.locales,
        [languageVersion]: {
          ...defaultBlockData.h5p.locales[languageVersion]
        }
      }
    });
  };

  const resetError = () => {
    setError(null);
  };

  const isTask =
    data && typeof data.isTask !== 'undefined' ? data.isTask : true;

  const pointsWeight =
    data.pointsWeight ?? defaultBlockData.h5p.pointsWeight;

  const isEditingLanguageVersion = Boolean(editorLanguageActive);

  return (
    <div className="embed">
      {!h5pUrl || showLanguageSpecificUploader ? (
        <div onClick={resetError}>
          <FileUploader
            allowedFileTypes={['.h5p']}
            disabled={isPreview}
            dragDrop={!isPreview}
            isH5P={true}
            icon="file"
            id={`file-uploader-${blockId}`}
            keyPrefix="uploads/compressed/"
            onUploadSuccess={handleUploadSuccess}
            onError={handleUploadError}
            placeholder={messages.editorBlockEmbedChooseH5PFile}
          />

          {error && (
            <div className="error">
              {messages.editorBlockEmbedH5PFileNotSupported}
            </div>
          )}
        </div>
      ) : (
        <div className="inner">
          <TaskPointWeightSelector
            blockId={blockId}
            blockName={messages.blocksH5P}
            disabled={isPreview || isEditingLanguageVersion}
            handleTaskClick={handleIsTask}
            isEmbed={true}
            isMandatory={isTask && data.isMandatory}
            isTask={isTask}
            pointsWeight={pointsWeight}
            onSelection={handlePointsWeight}
          />

          <EditorBlockEmbedPreview
            blockId={blockId}
            blockType="h5p"
            data={data}
            dataFromDefaultLanguage={shouldShowDefaultFile}
            disableIsMandatorySettings={isEditingLanguageVersion}
            hideNonTranslatableSettings={shouldShowDefaultFile}
            isPreview={isPreview}
            languageVersion={activeLanguage}
            loading={loading}
            style={{ marginTop: 10 }}
            updateData={updateData}
            {...(!editorLanguageActive
              ? {
                  customControls: (
                    <EditorBlockEmbedFileControls
                      file={{
                        title: data.locales[activeLanguage]?.file?.title,
                        url: data.locales[activeLanguage]?.file?.compressedUrl
                      }}
                      type="h5p"
                    />
                  )
                }
              : {})}
            {...stateProps}
          />
        </div>
      )}

      {editorLanguageActive ? (
        <EditorBlockTranslationReplace
          customStatusText={messages.blocksFileUsingLanguageSpecific}
          defaultStatusText={messages.blocksFileUsingDefaultLanguage}
          downloadableFileUrl={
            data.locales[activeLanguage]?.file?.compressedUrl
          }
          hasCustomValue={!shouldShowDefaultFile}
          icon={['far', 'file']}
          onCancel={handleReplaceCancel}
          onRemove={handleRemove}
          onReplace={handleReplace}
          showCancel={showLanguageSpecificUploader}
        />
      ) : null}

      {showIsTaskDialog && !isEditingLanguageVersion ? (
        <TaskIsTaskDialog onChoice={handleIsTask} />
      ) : null}

      <style jsx>{`
        .embed {
          background-color: ${theme.lightGrey};
          border-radius: ${theme.cardBorderRadius};
        }
        .inner {
          padding: 15px;
        }
      `}</style>
    </div>
  );
};

export default EditorBlockH5P;
